import { useState, useEffect, useCallback } from 'react';

/**
 * useApiList — generic hook for paginated, searchable API lists.
 * @param {Function} apiFn   — API service function: (token, params) => Promise
 * @param {string}   token   — JWT token from AppContext
 * @param {object}   extra   — extra static params (e.g. { role: 'Scout' })
 */
export default function useApiList(apiFn, token, extra = {}) {
  const [rows,       setRows]       = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [page,       setPage]       = useState(1);
  const [search,     setSearch]     = useState('');
  const [query,      setQuery]      = useState('');

  const load = useCallback(async (p = 1, q = '') => {
    setLoading(true); setError(null);
    try {
      const params = { page: p, limit: 20, ...extra, ...(q ? { search: q } : {}) };
      const res = await apiFn(token, params);
      setRows(res.data || []);
      setPagination(res.pagination || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiFn, token, JSON.stringify(extra)]); // eslint-disable-line

  useEffect(() => { load(1, ''); }, [load]);

  const submitSearch = useCallback((q) => {
    setQuery(q); setPage(1); load(1, q);
  }, [load]);

  const clearSearch = useCallback(() => {
    setSearch(''); setQuery(''); setPage(1); load(1, '');
  }, [load]);

  const goToPage = useCallback((p) => {
    setPage(p); load(p, query);
  }, [load, query]);

  const reload = useCallback(() => load(page, query), [load, page, query]);

  return {
    rows, pagination, loading, error,
    page, search, setSearch, query,
    submitSearch, clearSearch, goToPage, reload,
  };
}
